import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";

import useLocalization from "../../hooks/useLocalization";
import usePage from "../../hooks/usePage";
import useData from "../../hooks/useData";

import { Box, Stack, Button, TextField } from "@mui/material";

import Page from "./Page";
import Card from "../layout/Card";
import FileUpload from "../elements/FileUpload";
import Select from "../elements/Select";

const ClientImportExport = () => {
  const { getTitle } = useLocalization();
  const location = useLocation();
  const navigate = useNavigate();
  const { addToWorkQueue, getFromServer } = useData();
  const { showMessage } = usePage();

  const [currentImport, setCurrentImport] = useState(null);
  const [currentExport, setCurrentExport] = useState(null);
  const [projects, setProjects] = useState([]);
  const [projectId, setProjectId] = useState("all");
  const [callStatus, setCallStatus] = useState("all");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");

  const currentUserId = useSelector((state) => state.authentication.userId);
  const email = useSelector((state) => state.authentication.email);
  const entityName = location.state.entityName;
  const entityId = location.state.entityId;
  const name = location.state.name;

  useEffect(() => {
    const loadProjects = async () => {
      if (!entityId) {
        setProjects([]);
        return;
      }

      const results = await getFromServer({
        entityName: "Project",
        filter: { clientId: entityId },
      });

      const list = Array.isArray(results) ? results : [];
      list.sort((a, b) =>
        String(a.name || "").localeCompare(String(b.name || "")),
      );
      setProjects(list);
    };

    loadProjects();
  }, [entityId, getFromServer]);

  const handleExportSelection = async (selection) => {
    switch (selection) {
      case "skuTemplate":
        await queueFile("exportSkuImportTemplate");
        break;
      case "skuList":
        await queueFile("exportClientSkus");
        break;
      case "listingsTemplate":
        await queueFile("exportListingsTemplate");
        break;
      case "clientListings":
        await queueFile("exportClientListings");
        break;
      case "skuPricingTemplate":
        await queueFile("exportSkuPricingTemplate");
        break;
      case "calls":
        setCurrentExport("calls");
        break;
      default:
        break;
    }
  };

  const handleExportCalls = async () => {
    await addToWorkQueue("exportCalls", {
      entityName,
      entityId,
      email,
      userId: currentUserId,
      filter: {
        clientId: entityId,
        projectId,
        callStatus,
        fromDate,
        toDate,
      },
    });

    showMessage("Your file will be emailed to you shortly.");
  };

  const queueFile = async (workType) => {
    await addToWorkQueue(workType, {
      entityName,
      entityId,
      email,
      userId: currentUserId,
      filter: {
        clientId: entityId,
      },
    });

    showMessage("Your file will be emailed to you shortly.");
  };

  const handleImportSelection = (selection) => {
    setCurrentImport(selection);
  };

  const handleUploaded = () => {
    showMessage("You will receive an email on completion.");
  };

  const handleToolbarClick = (action) => {
    switch (action) {
      case "back":
        navigate("/client", { state: { clientId: entityId } });
        break;
      default:
        break;
    }
  };

  return (
    <Page
      pageName="Client Imports & Exports"
      entityName={name}
      includeToolbar
      items={[
        {
          name: "back",
          iconName: "ArrowCircleLeft",
        },
      ]}
      onToolItemClick={handleToolbarClick}
    >
      <Box
        sx={{ display: "flex" }}
        gap={1}
      >
        <Card
          title={getTitle("exports")}
          sx={{ width: "300px" }}
        >
          <Stack>
            <Box>
              <Button onClick={() => handleExportSelection("skuTemplate")}>
                SKU Template
              </Button>
            </Box>
            <Box>
              <Button
                onClick={() => handleExportSelection("skuPricingTemplate")}
              >
                SKU Pricing Template
              </Button>
            </Box>
            <Box>
              <Button onClick={() => handleExportSelection("skuList")}>
                Client SKU List
              </Button>
            </Box>
            <Box>
              <Button onClick={() => handleExportSelection("listingsTemplate")}>
                SKU Listings Template
              </Button>
            </Box>
            <Box>
              <Button onClick={() => handleExportSelection("clientListings")}>
                Client Listings
              </Button>
            </Box>
            <Box>
              <Button onClick={() => handleExportSelection("calls")}>
                Calls
              </Button>
            </Box>
          </Stack>
        </Card>

        {currentExport === "calls" && (
          <Card
            title="Calls Export"
            sx={{ width: "360px" }}
          >
            <Stack
              spacing={1}
              padding="10px"
            >
              <Select
                label="project"
                value={projectId}
                onChange={setProjectId}
                items={projects}
                includeNone
                noneLabel="All Projects"
              />

              <Select
                label="callStatus"
                value={callStatus}
                onChange={setCallStatus}
                items={[
                  { id: "future", name: "Future" },
                  { id: "incomplete", name: "Incomplete" },
                  { id: "complete", name: "Complete" },
                  { id: "expired", name: "Expired" },
                ]}
                includeNone
                noneLabel="All Statuses"
              />

              <TextField
                label="From Date"
                type="date"
                value={fromDate}
                onChange={(e) => setFromDate(e.target.value)}
                InputLabelProps={{ shrink: true }}
                size="small"
              />

              <TextField
                label="To Date"
                type="date"
                value={toDate}
                onChange={(e) => setToDate(e.target.value)}
                InputLabelProps={{ shrink: true }}
                size="small"
              />

              <Button
                variant="contained"
                onClick={handleExportCalls}
              >
                Export Calls
              </Button>
            </Stack>
          </Card>
        )}

        <Card
          title={getTitle("imports")}
          sx={{ width: "300px" }}
        >
          <Stack>
            <Box>
              <Button
                onClick={() => handleImportSelection("importSkusForClient")}
              >
                {getTitle("importSkusForClient")}
              </Button>
            </Box>
            <Box>
              <Button onClick={() => handleImportSelection("importSkuPricing")}>
                Import SKU Pricing
              </Button>
            </Box>
            <Box>
              <Button onClick={() => handleImportSelection("importListings")}>
                Import SKU Listings
              </Button>
            </Box>
          </Stack>
        </Card>

        {currentImport !== null && (
          <Card
            title={getTitle(currentImport)}
            sx={{ width: "300px" }}
          >
            <Box padding="10px">
              <FileUpload
                entityName={entityName}
                entityId={entityId}
                data={{
                  workType: currentImport,
                  entityName: entityName,
                  entityId: entityId,
                  userId: currentUserId,
                  email: email,
                }}
                onUploaded={handleUploaded}
              />
            </Box>
          </Card>
        )}
      </Box>
    </Page>
  );
};

export default ClientImportExport;
